import { exists } from './exists'

/**
 * 検査対象が存在する場合は変換関数を適用し、存在しない場合はデフォルト値を返します。
 *
 * @template T - 検査対象の型。
 * @template U - 変換後の型。
 * @param value - 検査対象。
 * @param fn - 変換関数。
 * @param defaultValue - デフォルト値。
 * @returns 変換後の値。
 */
export function applyOr<T, U>(
  value: T | null | undefined,
  fn: (value: T) => U,
  defaultValue: U,
): U

/**
 * 検査対象が存在する場合は変換関数を適用し、存在しない場合はデフォルト値を返します。
 *
 * @template T - 検査対象の型。
 * @template U - 変換後の型。
 * @param fn - 変換関数。
 * @param defaultValue - デフォルト値。
 * @returns 検査対象を引数に取り、変換後の値を返す関数。
 */
export function applyOr<T, U>(
  fn: (value: T) => U,
  defaultValue: U,
): (value: T | null | undefined) => U

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function applyOr<T, U>(
  ...args: [(value: T) => U, U] | [T | null | undefined, (value: T) => U, U]
): U | ((value: T | null | undefined) => U) {
  const applyOrInner = (
    value: T | null | undefined,
    fn: (value: T) => U,
    defaultValue: U,
  ): U => (exists(value) ? fn(value) : defaultValue)

  if (args.length === 2) {
    return (value: T | null | undefined): U => applyOrInner(value, ...args)
  }

  return applyOrInner(...args)
}
